/**
 * Admin waitlist view (/admin/waitlist). Lists everyone who joined the public
 * waitlist and offers a CSV export. Admin-gated by the API (403 otherwise).
 */

import { SignedIn, SignedOut, useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

interface Signup {
  id: string;
  email: string;
  source: string | null;
  referrer: string | null;
  createdAt: string;
}

function csvCell(value: string): string {
  // Quote if the value contains a comma, quote, or newline.
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

function AdminWaitlist() {
  const getToken = useSessionTokenGetter();
  const [signups, setSignups] = useState<Signup[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/admin/waitlist`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.status === 403) {
          setForbidden(true);
        } else if (res.ok) {
          const data = (await res.json()) as { signups: Signup[] };
          setSignups(data.signups);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const downloadCsv = () => {
    if (!signups) return;
    const header = 'Email,Source,Referrer,Signed up (UTC)';
    const lines = signups.map((s) =>
      [
        csvCell(s.email),
        csvCell(s.source ?? ''),
        csvCell(s.referrer ?? ''),
        csvCell(new Date(s.createdAt).toISOString()),
      ].join(','),
    );
    const blob = new Blob([`${header}\n${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `develio-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (forbidden) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center">
        <h1 className="text-xl font-semibold text-slate-900">Admin access required</h1>
        <p className="mt-2 text-sm text-slate-500">
          You do not have permission to view the waitlist.
        </p>
        <Link to="/dashboard" className="mt-4 inline-block text-sm text-brand-teal-700 underline">
          Back to dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-10">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Link to="/admin" className="text-xs text-slate-400 hover:text-slate-600">
            ← Admin
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">Waitlist</h1>
          <p className="mt-1 text-sm text-slate-500">
            {loading
              ? 'Loading…'
              : `${signups?.length ?? 0} ${signups?.length === 1 ? 'signup' : 'signups'}`}
          </p>
        </div>
        <button
          type="button"
          onClick={downloadCsv}
          disabled={!signups || signups.length === 0}
          className="shrink-0 rounded-lg bg-brand-teal-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Download CSV
        </button>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">Source</th>
              <th className="px-4 py-3 font-semibold">Signed up</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-slate-400">
                  Loading…
                </td>
              </tr>
            ) : !signups || signups.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-slate-400">
                  No signups yet.
                </td>
              </tr>
            ) : (
              signups.map((s) => (
                <tr key={s.id}>
                  <td className="px-4 py-3 font-medium text-slate-800">{s.email}</td>
                  <td className="px-4 py-3 text-slate-500">{s.source ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-500">
                    {new Date(s.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function WaitlistAdminRoute() {
  return (
    <>
      <SignedOut>
        <Navigate to="/sign-in" replace />
      </SignedOut>
      <SignedIn>
        <AdminWaitlist />
      </SignedIn>
    </>
  );
}
